import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import QRCode from 'qrcode';
import { useWallet, useThor } from '@vechain/dapp-kit-react'; // 🚀 WICHTIG: Verwende DAppKitProvider Hooks
import { connectVeWorld as connectVeWorldSingleton, logoutVeWorld, disconnectEverywhere as disconnectEverywhereSingleton, getSignClient, savePairing, clearPairing, findExistingSession } from '../utils/walletconnectClient'; // 🚀 WICHTIG: Singleton SignClient
import LedgerConnect from './LedgerConnect';
import LedgerIcon from './LedgerIcon';
import { apiUrl } from '../utils/apiBase';
import { trackPlatformActivity } from '../api.js';
import {
  sleep,
  isLedgerUsbBusyError,
  isLedgerUsbResetError,
  mapLedgerErrorMessage,
} from '../utils/ledgerTransportHelpers';
import ModalMotionShell from './ModalMotionShell';

const VeWorldMark = ({ className = 'w-6 h-6' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    role="img"
    aria-hidden="true"
  >
    <path
      d="M3.2 5.2l3.6 13.6h2.8L12 11.7l2.5 7.1h2.8l3.5-13.6h-2.9l-2 7.6L12.9 8h-1.8l-3.7 7.1-2-7.6H3.2z"
      fill="currentColor"
    />
    <path
      d="M3.8 5.8h2.2l2.1 8.3L11.2 7.6h1.6l2.9 6.6 2.1-8.3h2.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="0.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.45"
    />
  </svg>
);

const ChainLinkIcon = ({ className = 'w-6 h-6' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Gleiches Kettensymbol wie im Header-Login-Button */}
    <path
      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
    />
  </svg>
);

// 🚨 Browser-Polyfills für Node.js APIs
import { Buffer } from 'buffer';
import process from 'process';

// Globale Polyfills setzen
if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
  window.process = process;
}

const WALLETCONNECT_PROJECT_ID = import.meta.env.VITE_WC_PROJECT_ID || '60635421b58cacf2ec5083593e40ffcf';
const VEWORLD_WALLETCONNECT_ID = 'f714fdf911e330128463abe9684b8c02f492826db185afecef1e9070410efeec';

// 🆕 ECHTER WALLETCONNECT V2 MODAL - LAZY LOADING
async function ensureWcModal() {
  if (typeof window === 'undefined') return null;
  
  // Verwende globale Variable statt useRef (da außerhalb der Komponente)
  if (!window.wcModalInstance) {
    const { WalletConnectModal } = await import('@walletconnect/modal');
    window.wcModalInstance = new WalletConnectModal({
      projectId: WALLETCONNECT_PROJECT_ID,
      themeMode: 'dark',
      explorerRecommendedWalletIds: [VEWORLD_WALLETCONNECT_ID],
      explorerExcludedWalletIds: 'ALL'
      // optional: themeVariables, zIndex etc.
    });
  }
  return window.wcModalInstance;
}

const VEWORLD_ANDROID_PACKAGE = 'org.vechain.veworld.app';
const VEWORLD_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=org.vechain.veworld.app';
const VEWORLD_APP_STORE_URL = 'https://apps.apple.com/app/id6446854569';
const VEWORLD_INFO_PAGE = 'https://www.veworld.net/';

function openVeWorldDeepLink(pairingUri, options = {}) {
  if (typeof window === 'undefined' || !pairingUri) return;

  const { log = () => {}, onFallback } = options;

  const encoded = encodeURIComponent(pairingUri);
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  const veworldScheme = `veworld://wc?uri=${encoded}`;
  const androidIntent =
    `intent://wc?uri=${encoded}` +
    `#Intent;scheme=veworld;package=${VEWORLD_ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(VEWORLD_PLAY_STORE_URL)};end`;
  const wcScheme = pairingUri;

  const triggerNavigation = (href) => {
    const link = document.createElement('a');
    link.href = href;
    link.rel = 'noopener';
    link.target = '_self';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  try {
    if (isAndroid) {
      const timers = [];
      const cleanup = () => {
        timers.forEach(clearTimeout);
        document.removeEventListener('visibilitychange', visibilityHandler);
      };

      const visibilityHandler = () => {
        if (document.hidden) {
          log('VeWorld foreground detected after deep link');
        }
        cleanup();
      };

      document.addEventListener('visibilitychange', visibilityHandler);

      const steps = [
        {
          action: () => {
            log('VeWorld scheme attempted', { veworldScheme, visibility: document.visibilityState });
            triggerNavigation(veworldScheme);
          },
          delay: 600
        },
        {
          action: () => {
            log('VeWorld android intent (veworld scheme) attempted', { androidIntent, visibility: document.visibilityState });
            triggerNavigation(androidIntent);
          },
          delay: 700
        },
        {
          action: () => {
            log('WalletConnect wc scheme attempted', { wcScheme, visibility: document.visibilityState });
            triggerNavigation(wcScheme);
          },
          delay: 700
        },
        {
          action: () => {
            log('VeWorld android fallback prompt', {
              message: 'Consider enabling external app launches in Edge or open VeWorld from the Store.'
            });
            cleanup();
            if (typeof onFallback === 'function') {
              onFallback(VEWORLD_PLAY_STORE_URL);
            } else {
              window.location.href = VEWORLD_PLAY_STORE_URL;
            }
          }
        }
      ];

      const runStep = (index) => {
        if (document.visibilityState === 'hidden') {
          cleanup();
          return;
        }

        const step = steps[index];
        if (!step) {
          cleanup();
          return;
        }

        try {
          step.action();
        } catch (navigationError) {
          log('Deep link navigation error', { error: navigationError?.message || String(navigationError) });
        }

        if (step.delay != null) {
          const timer = setTimeout(() => runStep(index + 1), step.delay);
          timers.push(timer);
        }
      };

      runStep(0);

      return;
    }

    if (isIOS) {
      try {
        triggerNavigation(veworldScheme);
      } catch (schemeError) {
        log('VeWorld scheme navigation error', { error: schemeError?.message || String(schemeError) });
      }
      log('VeWorld iOS scheme attempted', { veworldScheme, visibility: document.visibilityState });

      setTimeout(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          log('VeWorld iOS fallback prompt', {});
          if (typeof onFallback === 'function') {
            onFallback(VEWORLD_APP_STORE_URL);
          }
        }
      }, 2000);

      return;
    }
  } catch (error) {
    log('Deep link navigation error', { error: error?.message || String(error) });
  }
}

// 🚀 WICHTIG: Verwende DAppKitProvider Hooks, nicht eigenen DAppKit!

const isMobileDevice = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod/i.test(ua);
})();

// 🚀 Telegram-Detection (verhindert Crashes im Telegram WebView)
const isTelegramWebView = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Telegram/i.test(ua);
})();

const useVeChainWallet = (onCloseCallback, options = {}) => {
  const { mobileLog = null } = options;
  const isMobile = options?.isMobile ?? isMobileDevice;
  // 🚀 WICHTIG: Verwende DAppKitProvider Hooks
  const { account, connect, source, setSource } = useWallet();
  const { thor } = useThor();
  
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [qrCodeUri, setQrCodeUri] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [veWorldPrepared, setVeWorldPrepared] = useState(null);
  const [isPreparingVeWorld, setIsPreparingVeWorld] = useState(false);
  const [veWorldPrepareError, setVeWorldPrepareError] = useState(null);
  const [showVeWorldFallback, setShowVeWorldFallback] = useState(false);
  const [veWorldFallbackUrl, setVeWorldFallbackUrl] = useState(VEWORLD_PLAY_STORE_URL);
  
  // 🆕 FUNKTION ZUM ERZWUNGENEN SCHLIEßEN DES MODALS - INNERHALB DES HOOKS
  const forceCloseModal = () => {
        
    // 🎯 Direkt den onClose Handler aufrufen
    if (typeof onCloseCallback === 'function') {
      onCloseCallback();
    }
    
    // 🎯 Zusätzlich: State zurücksetzen um sicherzustellen, dass das Modal geschlossen wird
    setConnectionStatus('pending');
    setQrCodeUri(null);
    setQrCodeImage(null);
    
    // 🎯 Kurze Verzögerung um sicherzustellen, dass das Modal geschlossen wird
    setTimeout(() => {
          }, 100);
  };
  
  // 🚀 WICHTIG: Reset aller States wenn Modal neu geöffnet wird
  const resetAllStates = () => {
        setConnectionStatus('disconnected');
    setQrCodeUri(null);
    setQrCodeImage(null);
  };
  
  // 🚀 WICHTIG: Reset nur QR-Code States (wird bei Modal-Öffnung aufgerufen)
  const resetQrCodeStates = () => {
        setQrCodeUri(null);
    setQrCodeImage(null);
      };
  
  const logMobileDebug = () => {};

  const requiredNamespaces = useMemo(() => ({
    vechain: {
      chains: [
        'vechain:b1ac3413d346d43539627e6be7ec1b4a',
        'vechain:87721b09ed2e15997f466536b20bb127'
      ],
      methods: [
        'thor_signCertificate',
        'thor_sendTransaction'
      ],
      events: []
    }
  }), []);

  const prepareVeWorldConnection = useCallback(async () => {
    if (account) {
            setConnectionStatus('connected');
      return { success: true, session: null, account };
    }

    if (veWorldPrepared || isPreparingVeWorld) {
      return veWorldPrepared;
    }

    setIsPreparingVeWorld(true);
    setVeWorldPrepareError(null);

    try {
      const result = await connectVeWorldSingleton(requiredNamespaces);
      setVeWorldPrepared(result);

      if (result.session) {
                setConnectionStatus('connected');
      }

      return result;
    } catch (error) {
            setVeWorldPrepareError(error);
      throw error;
    } finally {
      setIsPreparingVeWorld(false);
    }
  }, [account, veWorldPrepared, isPreparingVeWorld, requiredNamespaces]);

  const clearVeWorldPrepared = useCallback(() => {
    setVeWorldPrepared(null);
    setVeWorldPrepareError(null);
    setIsPreparingVeWorld(false);
  }, []);

  const connectVeWorld = async () => {
    try {
      setConnectionStatus('connecting');
            logMobileDebug('connectVeWorld invoked');
      
      // 🚀 WICHTIG: Verwende Singleton SignClient für Pairing-Reuse!
      
      // 🎯 1) Bestehende Session prüfen
      if (account) {
                setConnectionStatus('connected');
        return { success: true, account: account };
      }
      
      // 🎯 3) Singleton SignClient verwenden
            
      try {
        const result = await connectVeWorldSingleton(requiredNamespaces);
                logMobileDebug('connectVeWorldSingleton resolved', {
          hasUri: Boolean(result?.uri),
          isNewPairing: result?.isNewPairing,
          hasSession: Boolean(result?.session)
        });
        
        if (result.isNewPairing && result.uri) {
          if (isMobile && typeof window !== 'undefined') {
            // 🔗 Mobile Deeplink-Flow für VeWorld – verwende gemeinsamen Helper
            setConnectionStatus('pending');

            openVeWorldDeepLink(result.uri, {
              log: (message, context) =>
                logMobileDebug('connectVeWorld deep link', { message, context }),
              onFallback: (url = VEWORLD_PLAY_STORE_URL) => {
                setVeWorldFallbackUrl(url);
                setShowVeWorldFallback(true);
              }
            });

            logMobileDebug('Deep link triggered from connectVeWorld', { uriPresent: true });

            // Fertig – auf Approval warten, NICHT schließen
            return { deepLinkTriggered: true, uri: result.uri };
          } else {
            
            setQrCodeUri(result.uri);
            setQrCodeImage(null);
            setConnectionStatus('pending');

            forceCloseModal();

            try {
              const modal = await ensureWcModal();
              await modal?.openModal({ uri: result.uri });
                          } catch (modalError) {
                            const qrImage = await QRCode.toDataURL(result.uri, {
                width: 300,
                margin: 2,
                color: { dark: '#000000', light: '#FFFFFF' }
              });
              setQrCodeImage(qrImage);
            }

            try {
              const session = await result.approval();
              
              const client = await getSignClient();
              const justCreated = client.core.pairing.getPairings().slice(-1)[0];
              if (justCreated?.topic) {
                savePairing(justCreated.topic);
                              }

              setConnectionStatus('connected');

              try {
                const modal = await ensureWcModal();
                modal?.closeModal();
              } catch (closeError) {
                              }

              return {
                success: true,
                usesDAppKit: false,
                qrCodeReady: true,
                message: 'Echter WalletConnect v2 Modal geöffnet - Mit VeWorld Mobile scannen',
                session: session,
                account: session.namespaces?.vechain?.accounts?.[0]
                  ? (session.namespaces.vechain.accounts[0].includes(':') ? session.namespaces.vechain.accounts[0].split(':').pop() : session.namespaces.vechain.accounts[0])
                  : undefined
              };
            } catch (approvalError) {
              
              if (approvalError.message && approvalError.message.includes('Proposal expired')) {
                
                try {
                  const client = await getSignClient();
                  const sessionResult = await findExistingSession();

                  if (sessionResult.success && sessionResult.session) {
                                        const session = sessionResult.session;
                    const vechainAccount = session.namespaces?.vechain?.accounts?.[0];

                    if (vechainAccount) {
                      const account = vechainAccount.includes(':') ? vechainAccount.split(':').pop() : vechainAccount;
                      
                      setConnectionStatus('connected');

                      try {
                        const modal = await ensureWcModal();
                        modal?.closeModal();
                      } catch (closeError) {
                                              }

                      return {
                        success: true,
                        usesDAppKit: false,
                        qrCodeReady: true,
                        message: 'Session gefunden trotz Proposal-Ablauf',
                        session: session,
                        account: account
                      };
                    }
                  } else {
                                      }
                } catch (sessionCheckError) {
                                  }
              }

              setConnectionStatus('error');

              try {
                const modal = await ensureWcModal();
                modal?.closeModal();
              } catch (closeError) {
                              }

              throw approvalError;
            }
          }
        } else {
          // 🎯 Bestehendes Pairing wiederverwendet - kann Session oder QR-Code sein
                    
          if (result.session) {
            // 🎯 Direkte Session verfügbar
            setConnectionStatus('connected');
            logMobileDebug('Existing session reused', {
              account: result.session.namespaces?.vechain?.accounts?.[0]
            });
            
            // 🚀 WICHTIG: Debug Session-Struktur
                                                
            // 🚀 WICHTIG: Stelle sicher, dass das Pairing gespeichert ist
            try {
              const client = await getSignClient();
              const activePairings = client.core.pairing.getPairings().filter(p => p.active);
              if (activePairings.length > 0) {
                const pairingToSave = activePairings[0];
                savePairing(pairingToSave.topic);
                              }
            } catch (pairingError) {
                          }
            
            // 🎯 Sicherer Zugriff auf VeChain Account
            const vechainAccount = result.session.namespaces?.vechain?.accounts?.[0];
            if (!vechainAccount) {
                                        }
            
            return { 
              success: true, 
              account: vechainAccount,
              session: result.session // 🚀 WICHTIG: Session für späteren onSuccess Call verfügbar machen
            };
          } else if (result.uri && result.approval) {
            // 🎯 QR-Code für bestehendes Pairing erforderlich
                        
            // 🚀 WICHTIG: ABER: Wenn wir ein bestehendes Pairing haben, sollten wir KEINEN QR-Code generieren!
            // Das führt zu doppelten Einträgen in VeWorld!
            if (result.existingPairing) {
                                                        
              // 🎯 Versuche, das bestehende Pairing direkt zu verwenden
              try {
                const reuseResult = await reuseExistingPairing(result.existingPairing, requiredNamespaces);
                
                if (reuseResult.success && reuseResult.session) {
                                    
                  // 🚀 WICHTIG: Stelle sicher, dass das bestehende Pairing gespeichert ist
                  try {
                    const client = await getSignClient();
                    const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                    if (activePairings.length > 0) {
                      const pairingToSave = activePairings[0];
                      savePairing(pairingToSave.topic);
                                          }
                  } catch (pairingError) {
                                      }
                  
                  setConnectionStatus('connected');
                  return { 
                    success: true, 
                    usesDAppKit: false,
                    qrCodeReady: false,
                    message: 'Bestehendes Pairing erfolgreich aktiviert',
                    session: reuseResult.session,
                    account: reuseResult.session.namespaces?.vechain?.accounts?.[0]
                  };
                              } else if (reuseResult.success && reuseResult.canReuse && reuseResult.needsUserConfirmation && reuseResult.approval) {
                                
                // 🎯 Kein QR-Code anzeigen, nur Status setzen
                setConnectionStatus('pending');
                setQrCodeUri(null);
                setQrCodeImage(null);
                
                // 🚀 WICHTIG: Warte auf die Approval-Promise
                                try {
                  const session = await reuseResult.approval();
                                    
                  // 🚀 WICHTIG: Stelle sicher, dass das bestehende Pairing gespeichert ist
                  try {
                    const client = await getSignClient();
                    const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                    if (activePairings.length > 0) {
                      const pairingToSave = activePairings[0];
                      savePairing(pairingToSave.topic);
                                          }
                  } catch (pairingError) {
                                      }
                  
                  setConnectionStatus('connected');
                  return { 
                    success: true, 
                    usesDAppKit: false,
                    qrCodeReady: false,
                    message: 'Bestehendes Pairing erfolgreich aktiviert',
                    session: session,
                    account: session.namespaces?.vechain?.accounts?.[0]
                  };
                } catch (approvalError) {
                                    
                  // 🚀 FIX: Wenn "Proposal expired", prüfe ob Session trotzdem erstellt wurde!
                  if (approvalError.message && approvalError.message.includes('Proposal expired')) {
                                        
                    try {
                      const client = await getSignClient();
                      const sessionResult = await findExistingSession();
                      
                      if (sessionResult.success && sessionResult.session) {
                                                const session = sessionResult.session;
                        const vechainAccount = session.namespaces?.vechain?.accounts?.[0];
                        
                        if (vechainAccount) {
                          const account = vechainAccount.includes(':') ? vechainAccount.split(':').pop() : vechainAccount;
                                                    
                          setConnectionStatus('connected');
                          return { 
                            success: true, 
                            usesDAppKit: false,
                            qrCodeReady: false,
                            message: 'Session gefunden trotz Proposal-Ablauf',
                            session: session,
                            account: account
                          };
                        }
                      }
                    } catch (sessionCheckError) {
                                          }
                  }
                  
                  setConnectionStatus('error');
                  throw approvalError;
                }
              } else if (reuseResult.success && reuseResult.canReuse) {
                                
                // 🎯 Status auf "pending" setzen und dem Benutzer mitteilen
                setConnectionStatus('pending');
                setQrCodeUri(null);
                setQrCodeImage(null);
                
                return { 
                  success: true,
                  usesDAppKit: false,
                  qrCodeReady: false,
                  message: 'Bestätige die Verbindung in deiner VeWorld App (Fingerabdruck/PIN)',
                  pendingReconnect: true // 🚀 WICHTIG: Warte auf Benutzerbestätigung
                };
              } else {
                                    throw new Error(reuseResult.error);
                }
              } catch (error) {
                                throw error;
              }
            }
            
            // 🎯 Nur wenn KEIN bestehendes Pairing vorhanden ist, ECHTER WALLETCONNECT V2 MODAL öffnen
            if (isMobile && typeof window !== 'undefined') {
              openVeWorldDeepLink(
                result.uri,
                (message, context) => logMobileDebug(message, context)
              );

              setConnectionStatus('pending');

              try {
                const session = await result.approval();
                
                try {
                  const client = await getSignClient();
                  const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                  if (activePairings.length > 0) {
                    const pairingToSave = activePairings[0];
                    savePairing(pairingToSave.topic);
                                      }
                } catch (pairingError) {
                                  }

                setConnectionStatus('connected');

                if (typeof onCloseCallback === 'function') {
                  onCloseCallback();
                }

                return {
                  success: true,
                  usesDAppKit: false,
                  qrCodeReady: false,
                  message: 'Verbindung in VeWorld bestätigt',
                  session: session,
                  account: session.namespaces?.vechain?.accounts?.[0]
                };
              } catch (approvalError) {
                                logMobileDebug('Deep link approval failed (existing pairing)', { error: approvalError?.message });
                setConnectionStatus('error');
                throw approvalError;
              }
            }

            setQrCodeUri(result.uri);
            setQrCodeImage(null); // Eigenes Bild deaktivieren
            setConnectionStatus('pending');
            
            // 🆕 WICHTIG: "Wallet Verbinden" Feld ZUERST schließen
            // damit der Flow sauberer ist und nicht zwei Modals übereinander liegen
            setTimeout(() => {
              forceCloseModal();
            }, 300);
            
            // 🆕 ECHTER WALLETCONNECT V2 MODAL öffnen
            try {
              const modal = await ensureWcModal();
              await modal?.openModal({ uri: result.uri });
                          } catch (modalError) {
                            // Fallback: Eigenen QR-Code generieren
              const qrImage = await QRCode.toDataURL(result.uri, {
                width: 300,
                margin: 2,
                color: { dark: '#000000', light: '#FFFFFF' }
              });
              setQrCodeImage(qrImage);
            }
            
            // 🚀 WICHTIG: Jetzt auf die VeWorld-Bestätigung warten
                        try {
              const session = await result.approval();
                            
              // 🚀 WICHTIG: Stelle sicher, dass das bestehende Pairing gespeichert ist
              try {
                const client = await getSignClient();
                const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                if (activePairings.length > 0) {
                  const pairingToSave = activePairings[0];
                  savePairing(pairingToSave.topic);
                                  }
              } catch (pairingError) {
                              }
              
              setConnectionStatus('connected');
              
              // 🆕 Modal schließen
              try {
                const modal = await ensureWcModal();
                modal?.closeModal();
              } catch (closeError) {
                              }
              
              return { 
                success: true, 
                usesDAppKit: false,
                qrCodeReady: true,
                message: 'Echter WalletConnect v2 Modal geöffnet - Mit VeWorld Mobile scannen',
                session: session,
                account: session.namespaces?.vechain?.accounts?.[0]
              };
            } catch (approvalError) {
                            
              // 🚀 FIX: Wenn "Proposal expired", prüfe ob Session trotzdem erstellt wurde!
              if (approvalError.message && approvalError.message.includes('Proposal expired')) {
                                
                try {
                  const client = await getSignClient();
                  const sessionResult = await findExistingSession();
                  
                  if (sessionResult.success && sessionResult.session) {
                                        const session = sessionResult.session;
                    const vechainAccount = session.namespaces?.vechain?.accounts?.[0];
                    
                    if (vechainAccount) {
                      const account = vechainAccount.includes(':') ? vechainAccount.split(':').pop() : vechainAccount;
                                            
                      setConnectionStatus('connected');
                      
                      // 🆕 Modal schließen
                      try {
                        const modal = await ensureWcModal();
                        modal?.closeModal();
                      } catch (closeError) {
                                              }
                      
                      return { 
                        success: true, 
                        usesDAppKit: false,
                        qrCodeReady: true,
                        message: 'Session gefunden trotz Proposal-Ablauf',
                        session: session,
                        account: account
                      };
                    }
                  }
                } catch (sessionCheckError) {
                                  }
              }
              
              setConnectionStatus('error');
              
              // 🆕 Modal schließen bei Fehler
              try {
                const modal = await ensureWcModal();
                modal?.closeModal();
              } catch (closeError) {
                              }
              
              throw approvalError;
            }
          } else if (result.approval && !result.uri) {
            // 🚀 WICHTIG: NEUER FALL - Pairing reaktiviert, warte auf VeWorld-Bestätigung OHNE QR-Code
                        
            // 🎯 Kein QR-Code anzeigen, nur Status setzen
            setConnectionStatus('pending');
            setQrCodeUri(null);
            setQrCodeImage(null);
            
            // 🚀 WICHTIG: Jetzt auf die VeWorld-Bestätigung warten
                        try {
              const session = await result.approval();
                            
              // 🚀 WICHTIG: Stelle sicher, dass das bestehende Pairing gespeichert ist
              try {
                const client = await getSignClient();
                const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                if (activePairings.length > 0) {
                  const pairingToSave = activePairings[0];
                  savePairing(pairingToSave.topic);
                                  }
              } catch (pairingError) {
                              }
              
              setConnectionStatus('connected');
              return { 
                success: true, 
                usesDAppKit: false,
                qrCodeReady: false, // 🚀 WICHTIG: Kein QR-Code angezeigt
                message: 'Pairing reaktiviert - Bestätige in VeWorld App',
                session: session,
                account: session.namespaces?.vechain?.accounts?.[0]
              };
            } catch (approvalError) {
                            
              // 🚀 FIX: Wenn "Proposal expired", prüfe ob Session trotzdem erstellt wurde!
              if (approvalError.message && approvalError.message.includes('Proposal expired')) {
                                
                try {
                  const client = await getSignClient();
                  const sessionResult = await findExistingSession();
                  
                  if (sessionResult.success && sessionResult.session) {
                                        const session = sessionResult.session;
                    const vechainAccount = session.namespaces?.vechain?.accounts?.[0];
                    
                    if (vechainAccount) {
                      const account = vechainAccount.includes(':') ? vechainAccount.split(':').pop() : vechainAccount;
                                            
                      setConnectionStatus('connected');
                      return { 
                        success: true, 
                        usesDAppKit: false,
                        qrCodeReady: false,
                        message: 'Session gefunden trotz Proposal-Ablauf',
                        session: session,
                        account: account
                      };
                    }
                  }
                } catch (sessionCheckError) {
                                  }
              }
              
              setConnectionStatus('error');
              throw approvalError;
            }
          } else if (result.existingPairing && result.canReuse && !result.needsQrCode) {
            // 🚀 WICHTIG: NEUER FALL - Bestehendes Pairing kann direkt wiederverwendet werden (KEIN QR-Code!)
                        
            try {
              // 🎯 WICHTIG: Verwende die neue reuseExistingPairing Funktion
              // Diese ruft KEIN client.connect() auf und erstellt KEINEN neuen Eintrag
              const reuseResult = await reuseExistingPairing(result.existingPairing, requiredNamespaces);
              
              if (reuseResult.success && reuseResult.session) {
                                
                // 🚀 WICHTIG: Stelle sicher, dass das bestehende Pairing gespeichert ist
                try {
                  const client = await getSignClient();
                  const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                  if (activePairings.length > 0) {
                    const pairingToSave = activePairings[0];
                    savePairing(pairingToSave.topic);
                                      }
                } catch (pairingError) {
                                  }
                
                setConnectionStatus('connected');
                return { 
                  success: true, 
                  usesDAppKit: false,
                  qrCodeReady: false,
                  message: 'Bestehendes Pairing erfolgreich aktiviert',
                  session: reuseResult.session,
                  account: reuseResult.session.namespaces?.vechain?.accounts?.[0]
                };
              } else if (reuseResult.success && reuseResult.canReuse) {
                                
                // 🎯 Kein QR-Code anzeigen, nur Status setzen
                setConnectionStatus('pending');
                setQrCodeUri(null);
                setQrCodeImage(null);
                
                // 🚀 WICHTIG: Hier müssen wir einen anderen Weg finden
                // Da wir kein client.connect() aufrufen können, müssen wir
                // das bestehende Pairing als "wiederverwendbar" markieren
                                                
                // 🎯 Wir geben das bestehende Pairing zurück
                // Die aufrufende Komponente muss es dann direkt verwenden
                return { 
                  existingPairing: result.existingPairing, 
                  isNewPairing: false, 
                  canReuse: true,
                  needsQrCode: false // 🚀 WICHTIG: KEIN QR-Code erforderlich für bestehendes Pairing
                };
              } else {
                                
                if (reuseResult.isZombiePairing) {
                                    // 🎯 Pairing wurde bereits gelöscht, starte direkt neu
                  return await connectVeWorld(requiredNamespaces);
                } else {
                  // 🎯 Anderer Fehler - Pairing löschen und neu starten
                  await clearPairing();
                                    return await connectVeWorld(requiredNamespaces);
                }
              }
              
            } catch (error) {
                                          
              // 🎯 Pairing löschen und neu starten
              await clearPairing();
              
              // 🎯 Jetzt neu starten mit QR-Code
                            return await connectVeWorld(requiredNamespaces);
            }
          } else if (result.existingPairing && result.canReuse && result.needsQrCode) {
            // 🚀 WICHTIG: NEUER FALL - Bestehendes Pairing kann wiederverwendet werden, aber QR-Code erforderlich
                                    
            try {
              // 🎯 WICHTIG: Versuche, das bestehende Pairing direkt zu aktivieren
              // OHNE client.connect() - das würde einen neuen Eintrag erstellen
              const client = await getSignClient();
              
              // 🎯 WICHTIG: Versuche, das bestehende Pairing zu "pingen"
              // Das sollte das Pairing reaktivieren, ohne neue Session zu erstellen
              await client.core.pairing.ping({ topic: result.existingPairing });
                            
              // 🎯 Jetzt versuche, eine Session über das bestehende Pairing zu erstellen
              // ABER ohne client.connect() - stattdessen direkt über das Pairing
              const sessions = client.session.getAll();
              const activeSession = sessions.find(s => s.active);
              
              if (activeSession) {
                                
                // 🚀 WICHTIG: Stelle sicher, dass das bestehende Pairing gespeichert ist
                try {
                  const activePairings = client.core.pairing.getPairings().filter(p => p.active);
                  if (activePairings.length > 0) {
                    const pairingToSave = activePairings[0];
                    savePairing(pairingToSave.topic);
                                      }
                } catch (pairingError) {
                                  }
                
                setConnectionStatus('connected');
                return { 
                  success: true, 
                  usesDAppKit: false,
                  qrCodeReady: false,
                  message: 'Bestehendes Pairing erfolgreich aktiviert',
                  session: activeSession,
                  account: activeSession.namespaces?.vechain?.accounts?.[0]
                };
              } else {
                                                                
                // 🎯 WICHTIG: Pairing ist ungültig - lösche es und starte neu
                                await clearPairing();
                
                // 🎯 Jetzt neu starten mit QR-Code für KOMPLETT neues Pairing
                                return await connectVeWorld(requiredNamespaces);
              }
              
            } catch (error) {
                                          
              // 🎯 Pairing löschen und neu starten
              await clearPairing();
              
              // 🎯 Jetzt neu starten mit QR-Code
                            return await connectVeWorld(requiredNamespaces);
            }
          } else {
            throw new Error('Weder Session noch QR-Code noch Approval noch bestehendes Pairing verfügbar');
          }
        }
        
      } catch (connectError) {
                logMobileDebug('VeWorld connect threw error inside try', { error: connectError?.message });
        setConnectionStatus('error');
        throw connectError;
      }
      
    } catch (error) {
            logMobileDebug('VeWorld connect failed (outer catch)', { error: error?.message });
      setConnectionStatus('disconnected');
      return { success: false, error: error.message };
    }
  };



  const disconnect = async () => {
    // 🚀 WICHTIG: Verwende Singleton SignClient für Pairing-Reuse
    try {
      if (account) {
                
        // 🎯 WICHTIG: Nur Session trennen, Pairing behalten!
        await logoutVeWorld();
        
        setConnectionStatus('disconnected');
              }
      return { success: true };
    } catch (error) {
            return { success: false, error: error.message };
    }
  };

  // 🚀 WICHTIG: Funktion zum vollständigen Trennen der App
  const disconnectEverywhere = async () => {
    try {
      
      const result = await disconnectEverywhereSingleton();
      if (!result?.success) {
        return result;
      }
      setConnectionStatus('disconnected');

            return { success: true };
    } catch (error) {
            return { success: false, error: error.message };
    }
  };

  const updateConnectionStatus = useCallback((status) => {
    setConnectionStatus(status);
  }, []);

  return {
    account,
    source,
    requiredNamespaces,
    connect: connectVeWorld, // 🚀 WICHTIG: Verwende connectVeWorld
    disconnect,
    disconnectEverywhere, // 🚀 WICHTIG: Neue Funktion
    connectionStatus,
    qrCodeImage,
    qrCodeUri,
    resetAllStates, // 🚀 WICHTIG: Exportiere resetAllStates
    resetQrCodeStates, // 🚀 WICHTIG: Exportiere resetQrCodeStates
    prepareVeWorldConnection,
    clearVeWorldPrepared,
    veWorldPrepared,
    veWorldPrepareError,
    isPreparingVeWorld,
    showVeWorldFallback,
    setShowVeWorldFallback,
    veWorldFallbackUrl,
    setVeWorldFallbackUrl,
    updateConnectionStatus
  };
};

export default function WalletConnectModal({
  isOpen,
  onClose,
  onSuccess,
  resetQrCode = false,
  allowedWallets = null,
  uiVariant = 'auto',
  forceFreshVeWorldLogin = false,
  autoSelectWallet = null,
}) {
  const { language } = useLanguage();
  const { devBypass, loginWithMetaMask, applyLocalAuth, checkAuthStatus } = useAuth();
  const [isCoarsePointer, setIsCoarsePointer] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(pointer: coarse)').matches;
  });
  const isMobile = isMobileDevice || isCoarsePointer;

  /** Mobile-Debug-Overlay entfernt — No-Op für bestehende Aufrufe */
  const appendMobileLog = useCallback(() => {}, []);

  const { 
    account, 
    source, 
    requiredNamespaces,
    connect: connectVeWorld, 
    disconnect, 
    disconnectEverywhere,
    connectionStatus, 
    qrCodeImage, 
    qrCodeUri, 
    resetAllStates,
    resetQrCodeStates,
    prepareVeWorldConnection,
    clearVeWorldPrepared,
    veWorldPrepared,
    veWorldPrepareError,
    isPreparingVeWorld,
    showVeWorldFallback,
    setShowVeWorldFallback,
    veWorldFallbackUrl,
    setVeWorldFallbackUrl,
    updateConnectionStatus
  } =
    useVeChainWallet(onClose, { isMobile, mobileLog: isMobile ? appendMobileLog : null });
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [error, setError] = useState('');
  const [metamaskAccount, setMetamaskAccount] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      clearVeWorldPrepared();
      setShowVeWorldFallback(false);
      setVeWorldFallbackUrl(VEWORLD_PLAY_STORE_URL);
      return;
    }

    if (!isMobile) {
      clearVeWorldPrepared();
      setShowVeWorldFallback(false);
      setVeWorldFallbackUrl(VEWORLD_PLAY_STORE_URL);
      return;
    }

    // Mobile: kein stilles Prefetch / Session-Reuse beim Öffnen (optional später: eigener „letzte Wallet“-Flow)
    if (forceFreshVeWorldLogin) {
          }
    clearVeWorldPrepared();
  }, [isOpen, isMobile, forceFreshVeWorldLogin, clearVeWorldPrepared, setVeWorldFallbackUrl]);
  
  // 🎯 Draggable modal states
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  
  // 🚨 IMPROVED: Simplified connection state management
  const [isMetaMaskConnecting, setIsMetaMaskConnecting] = useState(false);
  
  // Get current account and source
  const currentAccount = account || metamaskAccount;
  const currentSource = account ? source : (metamaskAccount ? 'MetaMask Extension' : null);

  // 🚀 FIX: Error-Handler in Telegram komplett deaktivieren (verhindert alert() Overlays)
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || isTelegramWebView) return;

    const previousOnError = window.onerror;
    const previousUnhandled = window.onunhandledrejection;

    const handleError = (message, source, lineno, colno, error) => {
      const text = `Error: ${message} @ ${source || 'unknown'}:${lineno || 0}:${colno || 0}`;
      appendMobileLog(text);
      if (error?.stack) {
        appendMobileLog(`Stack: ${error.stack}`);
      }
      try {
        alert(text);
      } catch {
        // ignore alert failures
      }
      if (typeof previousOnError === 'function') {
        return previousOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      const text = `Unhandled rejection: ${reason?.message || reason}`;
      appendMobileLog(text);
      if (reason?.stack) {
        appendMobileLog(`Stack: ${reason.stack}`);
      }
      try {
        alert(text);
      } catch {
        // ignore alert failures
      }
      if (typeof previousUnhandled === 'function') {
        return previousUnhandled(event);
      }
      return false;
    };

    window.onerror = handleError;
    window.onunhandledrejection = handleUnhandledRejection;

    return () => {
      window.onerror = previousOnError;
      window.onunhandledrejection = previousUnhandled;
    };
  }, [isMobile, appendMobileLog]);



  // 🎯 Drag & Drop Event Handlers
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // only left click
    if (e.target.closest('button')) return; // don't start drag when pressing buttons

    e.preventDefault();
    const modalElement = e.currentTarget.closest('.modal-container');
    if (!modalElement) return;

    setIsDragging(true);

    const rect = modalElement.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Viewport boundaries
      const maxX = window.innerWidth - 400; // Modal width
      const maxY = window.innerHeight - 300; // Modal height
      
      setModalPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // 🎯 Event Listeners für Drag & Drop
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // 🚨 IMPROVED: Cleanup function to reset connection states
  const resetConnectionStates = () => {
    setIsConnecting(false);
    setIsMetaMaskConnecting(false);
    setSelectedWallet(null);
    setError('');
    // Don't reset account/source here as they might be needed for the success flow
    // But reset metamaskAccount if modal is closing
    if (!isOpen) {
      setMetamaskAccount(null);
    }
  };

  // 🚨 IMPROVED: Handle modal close with cleanup
  const handleClose = useCallback(() => {
    setIsConnecting(false);
    setIsMetaMaskConnecting(false);
    setSelectedWallet(null);
    setError('');

    // 🚨 CRITICAL: Don't reset account states when closing after successful login
    
    onClose();
  }, [onClose]);

  // ESC-Taste zum Schließen
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && handleClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  // 🚨 IMPROVED: Effect to reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetConnectionStates();
    }
  }, [isOpen]);
  
  // 🚀 WICHTIG: Effect to reset QR-Code states only when resetQrCode is true
  useEffect(() => {
    if (resetQrCode && isOpen) {
            resetQrCodeStates();
    }
  }, [resetQrCode, isOpen, resetQrCodeStates]);
  

  


  // 🚨 NEW: Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function to reset MetaMask states when component unmounts
            setIsMetaMaskConnecting(false);
      setMetamaskAccount(null);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const updatePointerState = (event) => setIsCoarsePointer(event.matches);
    setIsCoarsePointer(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updatePointerState);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(updatePointerState);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updatePointerState);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(updatePointerState);
      }
    };
  }, []);

  // 🚨 IMPROVED: Effect to handle successful connections
  // Auf Mobile KEIN Auto-Auth aus lingering useWallet/DAppKit-Account.
  // Der Mobile-VeWorld-Flow authentifiziert ausschließlich über finalizeWalletConnection().
  useEffect(() => {
    if (isMobile) return;

    if (forceFreshVeWorldLogin) {
            return;
    }

    if (account && source && source.includes('VeWorld')) {
            
      // 🚀 FIX: Wenn MetaMask auch aktiv ist, trenne MetaMask zuerst
      if (metamaskAccount) {
                setMetamaskAccount(null);
      }
      
      devBypass(account).then(loginResult => {
        if (loginResult.success) {
                    onSuccess && onSuccess(account, 'wallet-connect');
        } else {
                    setError('Backend authentication failed. Please try again.');
        }
      }).catch(error => {
                setError('Authentication error. Please try again.');
      });
    }
  }, [isMobile, account, source, metamaskAccount, devBypass, onSuccess, forceFreshVeWorldLogin]);

  // 🚨 REMOVED: Duplicate MetaMask authentication flow to prevent race conditions

  // 🚨 NEW: Function to manually reset MetaMask state
  const resetMetaMaskState = () => {
        setIsMetaMaskConnecting(false);
    setMetamaskAccount(null);
    setError('');
  };

  // 🔒 Ledger connection function (WebUSB: Transport immer schließen, Retry bei transient busy/reset)
  const connectLedger = async () => {
    setError('');
    
    const { default: TransportWebUSB } = await import('@ledgerhq/hw-transport-webusb');
    const { default: Eth } = await import('@ledgerhq/hw-app-eth');

    
    let lastError = null;

    for (let attempt = 1; attempt <= 2; attempt++) {
      let transport = null;

      try {
        
        transport = await TransportWebUSB.create();
        
        const eth = new Eth(transport);
        
        const vechainPath = "44'/818'/0'/0/0";
        const result = await eth.getAddress(vechainPath);

        
        if (!result.address || result.address.length !== 42 || !result.address.startsWith('0x')) {
          throw new Error('Invalid VeChain address format');
        }

        return {
          success: true,
          account: result.address,
          type: 'ledger',
          walletType: 'vechain',
        };
      } catch (error) {
        lastError = error;
        
        const shouldRetry =
          attempt < 2 && (isLedgerUsbBusyError(error) || isLedgerUsbResetError(error));

        if (shouldRetry) {
                    await sleep(350);
          continue;
        }

        throw new Error(mapLedgerErrorMessage(error, language));
      } finally {
        if (transport) {
          try {
            await transport.close();
                      } catch (closeError) {
                      }
        }
      }
    }

    throw new Error(mapLedgerErrorMessage(lastError, language));
  };

  // 🔒 Ledger login function (NORMALER User, kein Admin!)
  const loginWithLedger = async (ledgerAddress) => {
    try {
            
      const response = await fetch(apiUrl('/api/auth/ledger-login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          walletAddress: ledgerAddress,
          walletType: 'ledger'
        })
      });

      if (response.ok) {
        const result = await response.json();
        const user = result.user ?? result;

        trackPlatformActivity("login", {
          source: "ledger",
          walletAddress: ledgerAddress,
          userId: user?.userId || null,
          plan: user?.plan || "free",
        }).catch(() => {});

        return { success: true, user };
      } else {
        const error = await response.json();
                return { success: false, error: error.error || 'Ledger login failed' };
      }
    } catch (error) {
            return { success: false, error: error.message || 'Ledger login failed' };
    }
  };

  // 🚨 IMPROVED: MetaMask connection function with better error handling and delay
  const connectMetaMask = async () => {
    // Prevent multiple simultaneous connection attempts
    if (isMetaMaskConnecting) {
            return { 
        success: false, 
        message: language === 'en' 
          ? 'Connection already in progress. Please wait...' 
          : 'Verbindung läuft bereits. Bitte warten...'
      };
    }

    try {
            setIsMetaMaskConnecting(true);
      setError('');
      
      // Check if MetaMask is available
      if (!window.ethereum) {
        throw new Error(language === 'en' 
          ? 'MetaMask extension not installed. Please install MetaMask from metamask.io first.' 
          : 'MetaMask Extension nicht installiert. Bitte installieren Sie MetaMask von metamask.io zuerst.');
      }

      // FIRST: Check if already connected using eth_accounts (non-interactive, safe)
      try {
        const currentAccounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (currentAccounts && currentAccounts.length > 0) {
                    setMetamaskAccount(currentAccounts[0]);
          setIsMetaMaskConnecting(false);
          return { success: true, account: currentAccounts[0] };
        }
      } catch (checkError) {
              }

      // SECOND: Check if MetaMask is responsive and not busy
      try {
        // Use a simple, non-interactive method to check if MetaMask is responsive
        await window.ethereum.request({ method: 'eth_chainId' });
              } catch (chainError) {
        if (chainError.code === -32002) {
                    // Wait a bit and try again
          await new Promise(resolve => setTimeout(resolve, 500));
          try {
            await window.ethereum.request({ method: 'eth_chainId' });
                      } catch (retryError) {
            throw new Error('MetaMask is not responding. Please try again later.');
          }
        } else {
          throw chainError;
        }
      }

            
      // THIRD: Now request accounts (only if MetaMask is responsive)
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
            
      if (accounts && accounts.length > 0) {
                setMetamaskAccount(accounts[0]);
        setIsMetaMaskConnecting(false);
        return { success: true, account: accounts[0] };
      } else {
        throw new Error('No accounts returned from MetaMask');
      }
      
    } catch (error) {
            setIsMetaMaskConnecting(false);
      
      // Handle specific MetaMask error codes
      if (error.code === 4001) {
        return { 
          success: false, 
          message: language === 'en' 
            ? 'Connection cancelled by user. You can try again anytime.' 
            : 'Verbindung vom Benutzer abgebrochen. Sie können es jederzeit erneut versuchen.'
        };
      } else if (error.code === -32002) {
        // This should not happen anymore with our new approach, but just in case
                return { 
          success: false, 
          message: language === 'en' 
            ? 'MetaMask is busy. Please try again in a moment.' 
            : 'MetaMask ist beschäftigt. Bitte versuchen Sie es in einem Moment erneut.'
        };
      } else {
        // For other errors, show user-friendly message
        return {
          success: false,
          message: language === 'en'
            ? `MetaMask connection failed: ${error.message}`
            : `MetaMask-Verbindung fehlgeschlagen: ${error.message}`
        };
      }
    }
  };

  const walletOptions = useMemo(() => {
    const baseOptions = [
      {
        id: 'veworld',
        name: 'VeWorld',
        icon: <img src="/veworld.png" alt="VeWorld" className="w-full h-full object-contain" />,
        description:
          language === 'en'
            ? 'Official VeChain Mobile Wallet'
            : 'Offizielle VeChain Mobile Wallet',
        color: 'bg-slate-900',
        iconWrapperClass: 'w-9 h-9'
      },
      {
        id: 'metamask',
        name: 'MetaMask',
        icon: '🦊',
        description: language === 'de'
          ? 'Browser-Erweiterung (Desktop)'
          : 'Browser extension (desktop)',
        color: 'bg-gradient-to-r from-orange-500 to-red-600',
        iconWrapperClass: 'w-9 h-9'
      },
      {
        id: 'ledger',
        name: 'Ledger',
        icon: <LedgerIcon className="w-8 h-8" color="white" />,
        description:
          language === 'en'
            ? 'Hardware wallet (VeChain app required)'
            : 'Hardware-Wallet (VeChain-App erforderlich)',
        color: 'bg-black',
        iconWrapperClass: 'w-8 h-8'
      }
    ];

    if (Array.isArray(allowedWallets) && allowedWallets.length) {
      return baseOptions.filter((wallet) => allowedWallets.includes(wallet.id));
    }
    return baseOptions;
  }, [allowedWallets, language, isMobile]);

  const metamaskEnabled = walletOptions.some((wallet) => wallet.id === 'metamask');
  const forceDesktopLayout = uiVariant === 'desktop' || (uiVariant === 'auto' && !isCoarsePointer);

  const finalizeWalletConnection = useCallback(async (walletType, result) => {
    if (result && result.success && result.account) {
      if (walletType === 'veworld') {
        setShowVeWorldFallback(false);
        setVeWorldFallbackUrl(VEWORLD_PLAY_STORE_URL);
      }
      
      let loginResult;
      if (walletType === 'metamask') {
        loginResult = await loginWithMetaMask(result.account);
      } else if (walletType === 'ledger') {
        loginResult = await loginWithLedger(result.account);
      } else if (walletType === 'veworld') {
        const walletAddress = result.account.includes(':') ? result.account.split(':').pop() : result.account;
        setShowVeWorldFallback(false);
        setVeWorldFallbackUrl(VEWORLD_PLAY_STORE_URL);

        if (isMobile) {
          appendMobileLog(`VeWorld approval succeeded with account ${walletAddress}`);
        }

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('v3tax:expected_wallet_after_login', walletAddress);
        }

        const loginResultBackend = await devBypass(walletAddress, {
          skipLocalAuth: true,
          source: 'wallet-connect',
        });

        if (!loginResultBackend?.success) {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('v3tax:expected_wallet_after_login');
          }
          throw new Error(
            language === 'en'
              ? 'Backend session could not be created.'
              : 'Backend-Session konnte nicht erstellt werden.'
          );
        }

        const approvedWallet = walletAddress.toLowerCase();
        let statusData = {};
        let matched = false;

        for (let i = 0; i < 15; i += 1) {
          const statusRes = await fetch(apiUrl('/api/auth/status'), {
            credentials: 'include',
            cache: 'no-store',
          });
          statusData = statusRes.ok ? await statusRes.json().catch(() => ({})) : {};
          const backendWallet = statusData?.walletAddress?.toLowerCase?.() || null;

          if (isMobile) {
            appendMobileLog(
              `auth status poll ${i + 1}/15 ${JSON.stringify({
                approvedWallet,
                backendWallet,
                authenticated: !!statusData?.authenticated,
              })}`
            );
          }

          if (statusData?.authenticated && backendWallet === approvedWallet) {
            matched = true;
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 350));
        }

        if (!matched) {
          if (isMobile) {
            appendMobileLog(
              `VeWorld backend wallet mismatch after approval ${JSON.stringify({
                approvedWallet,
                backendWallet: statusData?.walletAddress || null,
              })}`
            );
          }
          try {
            await fetch(apiUrl('/api/auth/logout'), {
              method: 'POST',
              credentials: 'include',
            });
          } catch {
            /* ignore */
          }
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('v3tax:force_logged_out', '1');
            sessionStorage.removeItem('v3tax:expected_wallet_after_login');
          }
          localStorage.removeItem('v3tax_auth');
          await checkAuthStatus({ silent: true });
          throw new Error(
            language === 'en'
              ? 'VeWorld approved a wallet, but the backend session did not switch to the same wallet. Please try again.'
              : 'VeWorld hat eine Wallet bestätigt, aber die Backend-Session wurde nicht auf dieselbe Wallet umgestellt. Bitte erneut versuchen.'
          );
        }

        const finalAuth = {
          walletAddress,
          userId: statusData?.userId || loginResultBackend?.data?.userId || null,
          plan: statusData?.plan || loginResultBackend?.data?.plan || 'free',
          isDev: !!(statusData?.isDev ?? loginResultBackend?.data?.isDev),
          authenticated: true,
          loginTime: new Date().toISOString(),
          source: 'wallet-connect',
        };

        localStorage.setItem('v3tax_auth', JSON.stringify(finalAuth));
        applyLocalAuth(finalAuth);
        window.dispatchEvent(new Event('v3tax-auth'));

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('v3tax:expected_wallet_after_login');
          sessionStorage.removeItem('v3tax:mobile_fresh_login');
          sessionStorage.removeItem('v3tax:force_logged_out');
        }

        onSuccess && onSuccess(walletAddress, 'wallet-connect');
        handleClose();
        return;
      } else {
        loginResult = await devBypass(result.account);
      }

      if (loginResult && loginResult.success) {
                
        if (walletType === 'ledger') {
                  } else if (walletType === 'metamask') {
          setMetamaskAccount(result.account);
                  }
        
        onSuccess && onSuccess(result.account, walletType);
        handleClose();
      } else if (loginResult) {
        throw new Error(loginResult.error || 'Authentication failed');
      }
      return;
    }

    if (result?.usesDAppKit) {
            return;
    }
    if (result?.message) {
      setError(result.message);
      return;
    }
    throw new Error(result?.error || 'Connection failed');
  }, [applyLocalAuth, checkAuthStatus, devBypass, handleClose, isMobile, loginWithLedger, loginWithMetaMask, onSuccess, appendMobileLog, language, setMetamaskAccount, apiUrl]);

  const handleWalletConnect = async (walletType) => {
    // Prevent rapid successive calls
    if (isConnecting || (walletType === 'metamask' && isMetaMaskConnecting)) {
            return;
    }

    // 🚀 FIX: Beim neuen Login alle alten Auth-Daten löschen!
        localStorage.removeItem('v3tax_auth');
    
    // 🚀 FIX: Alte Wallet-States zurücksetzen
    if (account) {
            // Kein automatischer Disconnect hier.
      // Switch Account / echter Disconnect werden jetzt explizit in VeWorldMobileApp gesteuert.
    }
    if (metamaskAccount) {
            setMetamaskAccount(null);
    }

    setIsConnecting(true);
    setError('');
    setSelectedWallet(walletType);

    let skipFinalReset = false;

    try {
      let result;
      if (walletType === 'veworld' && isMobile) {
        appendMobileLog('VeWorld mobile login flow started (pairing reset only in Switch Account / startFreshMobileLogin)');

        clearVeWorldPrepared();
        resetAllStates();
        resetQrCodeStates();
        setShowVeWorldFallback(false);
        setVeWorldFallbackUrl(VEWORLD_PLAY_STORE_URL);

        const prepared = await connectVeWorldSingleton(requiredNamespaces, { forceFresh: true });

        if (prepared?.session) {
          appendMobileLog('force fresh mobile login unexpectedly returned existing session');
          throw new Error(
            language === 'en'
              ? 'Fresh VeWorld login returned an existing session. Please try again.'
              : 'Frischer VeWorld-Login lieferte eine bestehende Session. Bitte erneut versuchen.'
          );
        }

        if (!prepared?.uri || !prepared?.approval) {
          throw new Error('VeWorld pairing missing URI or approval promise');
        }

        skipFinalReset = true;
        updateConnectionStatus('pending');
        openVeWorldDeepLink(prepared.uri, {
          log: (message, context) => appendMobileLog(`${message}${context ? ` :: ${JSON.stringify(context)}` : ''}`),
          onFallback: (url = VEWORLD_PLAY_STORE_URL) => {
            setVeWorldFallbackUrl(url);
            setShowVeWorldFallback(true);
          }
        });
        appendMobileLog('VeWorld deep link triggered – awaiting fresh approval');

        prepared
          .approval()
          .then(async (session) => {
            try {
              const vechainAccount = session.namespaces?.vechain?.accounts?.[0];
              if (!vechainAccount) {
                throw new Error('No VeChain account in approval session');
              }
              const normalizedAccount = vechainAccount.includes(':')
                ? vechainAccount.split(':').pop()
                : vechainAccount;
              appendMobileLog(`VeWorld approval returned account ${normalizedAccount}`);
              await finalizeWalletConnection('veworld', {
                success: true,
                account: normalizedAccount,
                session,
              });
            } catch (approvalError) {
                            appendMobileLog(`VeWorld approval error: ${approvalError?.message || approvalError}`);
              setError(approvalError?.message || 'VeWorld approval failed');
            } finally {
              setIsConnecting(false);
              setSelectedWallet(null);
              updateConnectionStatus('disconnected');
            }
          })
          .catch((approvalError) => {
                        appendMobileLog(`VeWorld approval rejected: ${approvalError?.message || approvalError}`);
            setError(approvalError?.message || 'VeWorld approval failed');
            setIsConnecting(false);
            setSelectedWallet(null);
            updateConnectionStatus('error');
          });

        return;
      } else if (walletType === "veworld") {
        if (forceFreshVeWorldLogin) {
          
          try {
            await disconnectEverywhere();
                      } catch (e) {
                      }

          clearVeWorldPrepared();
          resetAllStates();
          resetQrCodeStates();

          localStorage.removeItem("v3tax_auth");
          localStorage.removeItem("walletconnect");
          localStorage.removeItem("walletconnect-v2");
          localStorage.removeItem("wc_v3tax_pairing_topic");

          Object.keys(localStorage).forEach((key) => {
            if (
              key.startsWith("wc@") ||
              key.startsWith("wc_") ||
              key.includes("walletconnect")
            ) {
              localStorage.removeItem(key);
            }
          });
        }

        result = await connectVeWorld();
      } else if (walletType === 'metamask') {
        result = await connectMetaMask();
      } else if (walletType === 'ledger') {
        // Ledger-Verbindung über die LedgerConnect-Komponente
        result = await connectLedger();
      }

      await finalizeWalletConnection(walletType, result);
    } catch (error) {
            
      // Simplified error handling
      let errorMessage = error.message || 'Connection failed';
      
      if (errorMessage.includes('User rejected') || errorMessage.includes('cancelled')) {
        errorMessage = language === 'en'
          ? 'Connection was cancelled. You can try again anytime.'
          : 'Verbindung wurde abgebrochen. Sie können es jederzeit erneut versuchen.';
      } else if (errorMessage.includes('already processing')) {
        errorMessage = language === 'en'
          ? 'Wallet is busy. Please wait and try again.'
          : 'Wallet ist beschäftigt. Bitte warten Sie und versuchen Sie es erneut.';
      }
      
      setError(errorMessage);
    } finally {
      if (!skipFinalReset) {
        setIsConnecting(false);
        setSelectedWallet(null);
        if (isMetaMaskConnecting) {
          setIsMetaMaskConnecting(false);
        }
      }
    }
  };

  const handleWalletConnectRef = useRef(handleWalletConnect);
  handleWalletConnectRef.current = handleWalletConnect;

  useEffect(() => {
    if (!isOpen) return;
    if (!autoSelectWallet) return;
    if (selectedWallet || isConnecting || isMetaMaskConnecting) return;

    const timer = setTimeout(() => {
      handleWalletConnectRef.current(autoSelectWallet);
    }, 80);

    return () => clearTimeout(timer);
  }, [
    autoSelectWallet,
    isConnecting,
    isMetaMaskConnecting,
    isOpen,
    selectedWallet,
  ]);

  const handleDisconnect = async () => {
    try {
      if (account) {
        await disconnect();
      }
      if (metamaskAccount) {
        setMetamaskAccount(null);
      }
      
      // Reset all connection states
      resetConnectionStates();
      
          } catch (error) {
            // Even if disconnect fails, reset local states
      resetConnectionStates();
    }
  };

  // 🚀 FIX: Cleanup-Funktion für doppelte Wallets
  const cleanupDuplicateWallets = () => {
        
    // Prüfe localStorage
    const authData = localStorage.getItem('v3tax_auth');
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
                
        // Prüfe ob mehrere Accounts aktiv sind
        if ((account && parsed.walletAddress !== account) || 
            (metamaskAccount && parsed.walletAddress !== metamaskAccount)) {
                                                  
          // Lösche Auth wenn nicht mit aktuellem Account übereinstimmt
          if (account && parsed.walletAddress !== account) {
                        localStorage.removeItem('v3tax_auth');
          } else if (metamaskAccount && parsed.walletAddress !== metamaskAccount) {
                        localStorage.removeItem('v3tax_auth');
          }
        }
      } catch (e) {
              }
    }
    
        // Prüfe ob beide Wallets gleichzeitig aktiv sind
        if (account && metamaskAccount) {
                                                  
          // 🚀 FIX: MetaMask trennen wenn VeWorld aktiv ist (VeWorld hat Priorität)
                    setMetamaskAccount(null);
        }
  };

  // 🚀 FIX: Cleanup beim Öffnen des Modals
  useEffect(() => {
    if (isOpen) {
      cleanupDuplicateWallets();
    }
  }, [isOpen, account, metamaskAccount]);

  // Wenn bereits verbunden, zeige Connected State
  if ((connectionStatus === 'connected' && account) || metamaskAccount) {
    return (
      <ModalMotionShell
        isOpen={isOpen}
        onClose={handleClose}
        zIndexClass="z-50"
        backdropTintClass="bg-black/70"
      >
        <div 
          className="modal-container bg-white rounded-xl shadow-lg border border-gray-200 p-6 max-w-lg mx-auto"
          style={{
            position: 'fixed',
            top: modalPosition.y || '50%',
            left: modalPosition.x || '50%',
            transform: modalPosition.x || modalPosition.y ? 'none' : 'translate(-50%, -50%)',
            cursor: isDragging ? 'grabbing' : 'default'
          }}
        >
          {/* 🎯 Draggable Header */}
          <div
            className="modal-header drag-handle flex justify-between items-center mb-4 cursor-move"
            onMouseDown={handleMouseDown}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {t('walletConnected', language) || (language === 'en' ? 'Wallet Connected' : 'Wallet Verbunden')}
            </h3>
            <div className="drag-handle flex flex-col gap-1 cursor-move">
              <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
              <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
              <div className="w-4 h-0.5 bg-gray-400 rounded"></div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="text-2xl">✅</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-sm text-gray-600 mb-1">
                {language === 'en' ? 'Connected via' : 'Verbunden über'}: <span className="font-medium">{currentSource}</span>
              </div>
              <div className="font-mono text-sm text-gray-800 break-all">
                {currentAccount?.slice(0, 6)}...{currentAccount?.slice(-4)}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDisconnect}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {language === 'en' ? 'Disconnect' : 'Trennen'}
              </button>
              <button
                onClick={() => onSuccess && onSuccess(currentAccount, 'wallet-connect')}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {language === 'en' ? 'Continue' : 'Weiter'}
              </button>
            </div>
          </div>
        </div>
      </ModalMotionShell>
    );
  }

  // Wallet Selection Interface
  return (
    <ModalMotionShell
      isOpen={isOpen}
      onClose={handleClose}
      zIndexClass="z-50"
      backdropTintClass="bg-black/70"
    >
      <div 
        role="dialog"
        aria-modal="true"
        aria-labelledby="wc-title"
        aria-describedby="wc-desc"
        className={`modal-container ${forceDesktopLayout ? 'walletconnect-panel' : ''} bg-white rounded-xl shadow-lg border border-gray-200 p-6 max-w-lg mx-auto`}
        style={{
          position: 'fixed',
          top: modalPosition.y || '50%',
          left: modalPosition.x || '50%',
          transform: modalPosition.x || modalPosition.y ? 'none' : 'translate(-50%, -50%)',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        {/* 🎯 Draggable Header */}
        <div
          className="modal-header drag-handle flex items-center justify-between mb-5 cursor-move border-b border-gray-100 pb-3"
          onMouseDown={handleMouseDown}
        >
          {/* Titel */}
          <div className="flex items-center gap-3">
            <div className="text-left">
              <h3 id="wc-title" className="text-base sm:text-lg font-semibold text-gray-900">
                {language === 'en' ? 'Connect your wallet' : 'Wallet verbinden'}
              </h3>
              <p className="mt-0.5 text-xs text-gray-500 hidden sm:block">
                {language === 'en'
                  ? 'Secure wallet login via VeWorld, MetaMask or Ledger'
                  : 'Sicherer Wallet-Login mit VeWorld, MetaMask oder Ledger'}
              </p>
            </div>
          </div>

          {/* Drag-Handle + Close */}
          <div className="flex items-center gap-2">
            <div className="drag-handle hidden sm:flex flex-col gap-1 cursor-move">
              <div className="w-4 h-0.5 bg-gray-300 rounded" />
              <div className="w-4 h-0.5 bg-gray-300 rounded" />
              <div className="w-4 h-0.5 bg-gray-300 rounded" />
            </div>
            <button
              onClick={handleClose}
              className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Intro-Icon + Text mittig im Modal — jetzt mit Hover-Animation */}
        <div className="flex flex-col items-center text-center mb-6">
          <div
            className="relative mb-4 group cursor-pointer select-none
                       focus-visible:outline-none focus-visible:ring-4
                       focus-visible:ring-blue-400/40 rounded-2xl"
            tabIndex={0}
            aria-hidden="true"
          >
            {/* weiches Glow hinter dem Badge (erscheint nur bei Hover/Focus) */}
            <div
              className="absolute inset-0 rounded-2xl bg-blue-500/20 blur-xl
                         opacity-0 group-hover:opacity-100
                         transition-opacity duration-300"
              aria-hidden="true"
            />
            {/* Badge selbst: kleiner Lift + Scale auf Hover */}
            <div
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600
                         shadow-md flex items-center justify-center
                         transition-transform duration-200 ease-out
                         group-hover:-translate-y-0.5 group-hover:scale-105
                         motion-reduce:transition-none motion-reduce:transform-none"
            >
              <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                <ChainLinkIcon className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>

          <p id="wc-desc" className="max-w-xs sm:max-w-sm text-gray-600 text-sm leading-snug">
            {language === 'en'
              ? 'Choose your preferred login method to connect your wallet.'
              : 'Wählen Sie Ihre bevorzugte Login-Methode, um Ihre Wallet zu verbinden.'}
          </p>
        </div>

        {error && !qrCodeImage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <span>⚠️</span>
              <span className="text-sm whitespace-pre-line">{error}</span>
            </div>
          </div>
        )}

        {/* ECHTER WALLETCONNECT V2 QR-CODE - MIT ECHTEM DESIGN */}
        {qrCodeImage && (
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-auto border border-gray-200 shadow-xl mb-6">
            <div className="text-center">
              {/* WalletConnect Header mit dem gleichen Kettensymbol wie im App-Header */}
              <div className="flex flex-col items-center justify-center mb-6 gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <ChainLinkIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">WalletConnect</h3>
              </div>
              
              {/* Echter QR Code mit WalletConnect v2 Design */}
              <div className="mb-6">
                <div className="text-sm text-gray-600 mb-4 font-medium">Scan with your mobile wallet</div>
                <div className="relative inline-block">
                  <img 
                    src={qrCodeImage} 
                    alt="WalletConnect QR Code" 
                    className="w-72 h-72 border-4 border-gray-100 rounded-2xl shadow-lg"
                  />
                  {/* Chain Link Icon in der Mitte */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white rounded-full p-4 shadow-xl border-4 border-white">
                      <ChainLinkIcon className="w-10 h-10 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Echte Desktop Wallets wie im WalletConnect v2 Screenshot */}
              <div className="mb-6">
                <div className="text-sm text-gray-600 mb-4 font-medium">Or connect with a desktop wallet</div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center group cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md group-hover:shadow-lg transition-all duration-200">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                    <div className="text-xs text-gray-700 font-medium">MetaMask</div>
                    <div className="text-xs text-blue-500 font-semibold">RECENT</div>
                  </div>
                  
                  <div className="text-center group cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md group-hover:shadow-lg transition-all duration-200">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                    <div className="text-xs text-gray-700 font-medium">Ledger Live</div>
                  </div>
                  
                  <div className="text-center group cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md group-hover:shadow-lg transition-all duration-200">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                    <div className="text-xs text-gray-700 font-medium">Zerion</div>
                  </div>
                  
                  <div className="text-center group cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-gray-300 to-gray-400 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md group-hover:shadow-lg transition-all duration-200">
                      <svg className="w-7 h-7 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                    <div className="text-xs text-gray-700 font-medium">View All</div>
                  </div>
                </div>
              </div>
              
              {/* Echter Close Button */}
              <button
                onClick={() => {
                  setQrCodeImage(null);
                  setQrCodeUri(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors duration-200"
              >
                {language === 'en' ? 'Close' : 'Schließen'}
              </button>
            </div>
          </div>
        )}
        
        {/* 🆕 ECHTER WALLETCONNECT V2 MODAL */}
        {/* Temporär deaktiviert wegen Class Constructor Fehler */}
        {/* <WCModal config={modalConfig} /> */}

        {/* 🚀 WICHTIG: Loading State für QR-Code Generation */}
        {isConnecting && selectedWallet === 'veworld' && !qrCodeImage && !isMobile && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4">
            <div className="text-center">
              <div className="flex items-center gap-2 mb-3 justify-center">
                <span>⏳</span>
                <span className="text-sm font-medium">
                  {language === 'en' 
                    ? 'Generating QR-Code...' 
                    : 'QR-Code wird generiert...'
                  }
                </span>
              </div>
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          </div>
        )}

        {isMobile && selectedWallet === 'veworld' && connectionStatus === 'pending' && (
          <div className="bg-blue-900/90 text-white px-4 py-4 rounded-lg mb-4 text-center space-y-2">
            <div className="text-base font-semibold">
              {language === 'en'
                ? 'Switching to VeWorld...'
                : 'Wechsle zur VeWorld App...'}
            </div>
            <div className="text-sm text-blue-100">
              {language === 'en'
                ? 'Confirm the connection in your VeWorld app. Return here once approved.'
                : 'Bestätige die Verbindung in deiner VeWorld App und kehre dann hierher zurück.'}
            </div>
            <button
              type="button"
              onClick={() => {
                setIsConnecting(false);
                setSelectedWallet(null);
                setConnectionStatus('disconnected');
                setShowVeWorldFallback(false);
              }}
              className="mt-2 inline-flex items-center justify-center w-full bg-white/10 hover:bg-white/20 text-sm font-medium text-white py-2 px-3 rounded-lg transition-colors"
            >
              {language === 'en' ? 'Cancel' : 'Abbrechen'}
            </button>
            {showVeWorldFallback && (
              <div className="mt-3 space-y-2">
                <div className="text-xs text-blue-200">
                  {language === 'en'
                    ? 'If VeWorld did not open automatically, ensure external apps are allowed (Edge: Settings → Privacy & security → Site permissions → External apps) or open it manually:'
                    : 'Falls VeWorld sich nicht automatisch geöffnet hat, erlaube externe Apps (Edge: Einstellungen → Datenschutz & Sicherheit → Websiteberechtigungen → Externe Apps) oder öffne sie manuell:'}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    appendMobileLog('VeWorld fallback button clicked');
                    window.location.href = veWorldFallbackUrl;
                  }}
                  className="w-full inline-flex items-center justify-center bg-white text-blue-800 font-semibold py-2 px-3 rounded-lg transition-colors hover:bg-blue-50"
                >
                  {language === 'en'
                    ? (veWorldFallbackUrl.includes('apple')
                        ? 'Open VeWorld in App Store'
                        : 'Open VeWorld in Play Store')
                    : (veWorldFallbackUrl.includes('apple')
                        ? 'VeWorld im App Store öffnen'
                        : 'VeWorld im Play Store öffnen')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          {walletOptions.map((wallet) => {
            const isBusy =
              (isConnecting && selectedWallet === wallet.id) ||
              (metamaskEnabled && isMetaMaskConnecting && wallet.id === 'metamask');

            return (
              <button
                key={wallet.id}
                onClick={() => handleWalletConnect(wallet.id)}
                disabled={isBusy}
                data-wallet={wallet.id}
                className={`group w-full p-4 rounded-xl border border-gray-200/90 bg-white transition-all duration-200 ${
                  isBusy
                    ? 'opacity-75 cursor-wait'
                    : 'hover:border-blue-300 hover:bg-blue-50/40 hover:shadow-md hover:-translate-y-0.5'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Icon-Tile */}
                  <div
                    className={`
                      w-14 h-14 ${wallet.color}
                      rounded-2xl flex items-center justify-center
                      text-white text-2xl shadow-sm
                      transition-all duration-200
                      group-hover:scale-[1.03]
                    `}
                  >
                    {isBusy ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : typeof wallet.icon === 'string' ? (
                      <span className="leading-none">{wallet.icon}</span>
                    ) : (
                      <div className={`${wallet.iconWrapperClass ?? 'w-9 h-9'} flex items-center justify-center`}>
                        {wallet.icon}
                      </div>
                    )}
                  </div>

                  {/* Text-Bereich */}
                  <div className="flex-1 text-left space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900">{wallet.name}</div>
                      {wallet.id === 'veworld' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          {language === 'en' ? 'Recommended' : 'Empfohlen'}
                        </span>
                      )}
                    </div>

                    {wallet.id === 'veworld' ? (
                      <div className="text-sm text-gray-600 leading-snug">
                        {isMobile ? (
                          // Mobile: kurz und ohne doppeltes "VeWorld"
                          language === 'en'
                            ? 'Mobile wallet (deep link)'
                            : 'Mobile Wallet (Deep Link)'
                        ) : (
                          // Desktop: nur Beschreibung + QR-Code in einer Zeile
                          language === 'en'
                            ? 'Connect via WalletConnect (QR code)'
                            : 'Verbinde über WalletConnect (QR-Code)'
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">{wallet.description}</div>
                    )}
                  </div>

                  {/* Arrow */}
                  <div className="text-blue-500 group-hover:text-blue-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* 🚨 NEW: Connection status and help text */}
        {(isConnecting || (metamaskEnabled && isMetaMaskConnecting)) && (
          <div className="mt-4 text-center">
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
              <div className="flex items-center gap-2 justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">
                  {metamaskEnabled && isMetaMaskConnecting
                    ? (language === 'en' 
                        ? 'Waiting for MetaMask response...' 
                        : 'Warte auf MetaMask-Antwort...')
                    : (language === 'en' 
                        ? 'Connecting to wallet...' 
                        : 'Verbinde mit Wallet...')
                  }
                </span>
              </div>
              {metamaskEnabled && isMetaMaskConnecting && (
                <div className="mt-2 text-xs text-blue-600">
                  {language === 'en'
                    ? '💡 If MetaMask popup doesn\'t appear, check your browser\'s popup blocker'
                    : '💡 Falls das MetaMask-Popup nicht erscheint, überprüfen Sie den Popup-Blocker Ihres Browsers'
                  }
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🚨 NEW: MetaMask reset button when there are errors */}
        {metamaskEnabled && error && error.includes('MetaMask') && (
          <div className="mt-4 text-center">
            <button
              onClick={resetMetaMaskState}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
            >
              {language === 'en' ? '🔄 Reset MetaMask State' : '🔄 MetaMask-Status zurücksetzen'}
            </button>
            <div className="mt-2 text-xs text-gray-600">
              {language === 'en'
                ? 'Click this if MetaMask seems stuck or unresponsive'
                : 'Klicken Sie hier, wenn MetaMask hängen bleibt oder nicht reagiert'
              }
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {language === 'en' ? (
              <>By connecting, you agree to our <a href="/terms" className="underline">Terms of Service</a>.</>
            ) : (
              <>Durch die Verbindung stimmen Sie unseren <a href="/agb" className="underline">Nutzungsbedingungen</a> zu.</>
            )}
          </p>
        </div>
      </div>
    </ModalMotionShell>
  );
}
